import generateClassNames from '@/utils/generate-class-names'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import PigSpinner from '../pig-spinner'
import './button.scss'

const classes = generateClassNames('btn', [
  'icon',
  'label',
  'loading-indicator',
])

export type ButtonProps<ComponentType extends React.ElementType = 'button'> =
  React.ComponentPropsWithoutRef<ComponentType> & {
    color?: 'primary' | 'white' | 'inherit'
    component?: ComponentType
    endIcon?: React.ReactNode
    fullWidth?: boolean
    loading?: boolean
    size?: 'xs' | 'sm' | 'md' | 'lg'
    startIcon?: React.ReactNode
    textAlign?: 'center' | 'left' | 'right'
    variant?: 'default' | 'neutral' | 'outline' | 'transparent'
  }

const Button = forwardRef(function Button<
  ComponentType extends React.ElementType,
>(props: ButtonProps<ComponentType>, ref: React.ForwardedRef<any>) {
  const {
    children,
    className,
    color,
    component: Component = 'button',
    disabled,
    endIcon,
    fullWidth,
    loading,
    size = 'md',
    startIcon,
    textAlign,
    variant = 'default',
    ...other
  } = props

  return (
    <Component
      ref={ref}
      className={clsx(
        classes.root,
        `${classes.root}--${variant}`,
        `${classes.root}--${size}`,
        textAlign && `${classes.root}--align-${textAlign}`,
        color && `${classes.root}--color-${color}`,
        fullWidth && `${classes.root}--full-width`,
        loading && `${classes.root}--loading`,
        className
      )}
      disabled={disabled || loading}
      {...other}
    >
      <>
        {startIcon && <span className={classes.icon}>{startIcon}</span>}
        <span className='btn__label'>{children}</span>
        {endIcon && <span className={classes.icon}>{endIcon}</span>}
        {loading ? (
          <span className={classes['loading-indicator']}>
            <PigSpinner />
          </span>
        ) : null}
      </>
    </Component>
  )
}) as <ComponentType extends React.ElementType = 'button'>(
  props: ButtonProps<ComponentType> & { ref?: React.Ref<any> }
) => React.ReactElement

export default Button
